import type { IAggFuncResult, TooltipCallbackParams } from "ag-grid-community"
import { addDays } from "date-fns"
import { flatten, max, min, sum, sumBy } from "lodash"
import { createElement } from "react"

import { IncomingOrderCellRenderer } from "~/components/purchaseOrder/incomingOrders/IncomingOrderCellRenderer"
import CustomAgGridTooltip from "~/components/reusable/table/tooltips/CustomTooltip"
import type { ICollectionDetails } from "~/hooks/useCollections"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import {
  compareWithNullsLast,
  createDateColumn,
  createNumberColumn,
  createSumColumn,
} from "~/utils/columnTemplates"
import { getStartOfCurrentWeekUTC } from "~/utils/datetime"
import { filterIncomingOrders } from "~/utils/incomingOrdersUtils"
import { isLifecyclePhasedOut } from "~/utils/lifecycleUtils"
import {
  availabilityTodayFromTotals,
  availabilityTodayTotals,
  extractVariantSummaries,
  locationAvailability,
  ProductTableColumn,
} from "~/utils/productTableUtils"
import { availabilityOf } from "~/utils/stockProjection"
import { filterDefined } from "~/utils/tsUtils"
import {
  calculateWeeksOnHand,
  clampWeeksOnHand,
  formatWeeksOnHand,
  getWeeksOnHandExplanation,
} from "~/utils/weeksOnHand"

import { getSkuStockLevels } from "../utils/stockLevelUtils"
import { leafVariantIds, mutedWhenPhasedOut, phasedOutTooltip } from "./cellHelpers"
import type { ColumnSpec } from "./types"

/** Forecast demand for one SKU between two dates; `null` means an open-ended bound. */
export type GetSkuForecastsBetweenDates = (
  skuId: string,
  warehouseIds: string[] | undefined,
  firstDate: Date | null,
  secondDate: Date | null,
) => number

export interface StockColumnCtx {
  currency: string | undefined
  /** End of the overstock window: today plus the company's overstock threshold. */
  thresholdDay: Date
  collections: ICollectionDetails[] | undefined
  collectionIds: number[] | undefined
  collectionPeriod: { startDate: Date; endDate: Date } | undefined
  hasExternalPurchaseOrderCollectionId: boolean | undefined
}

export type StockGridContext = {
  warehouseId: string | undefined
  getSkuForecastsBetweenDates: GetSkuForecastsBetweenDates
}

type StockSpec = ColumnSpec<IVariantSummary, StockColumnCtx, StockGridContext>

const UNAVAILABLE_CELL_CLASS =
  "text-fg-muted underline decoration-dotted underline-offset-2 cursor-help"

const numericAsc = (a: number, b: number) => a - b

/** The finite weeks-on-hand a cell sorts by; a group sorts by its lowest known value. */
const finiteOrNull = (value: unknown) => {
  const raw = Array.isArray(value) ? min(value as number[]) : (value as number | null | undefined)
  return raw != null && isFinite(raw) ? raw : null
}

const storageLocationTooltip = (params: TooltipCallbackParams<IVariantSummary>) => {
  const variants = filterDefined(params.node?.allLeafChildren?.map((node) => node.data))
  const storageLocationData = params.value

  return { variants, storageLocationData }
}

function overstockUnitsBySku(
  row: IVariantSummary,
  ctx: StockColumnCtx,
  grid: StockGridContext,
): { units: number; costPrice: number }[] {
  if (!row.stockLevelDetails) return []
  return getSkuStockLevels(row.stockLevelDetails, row.skuIds ?? []).map(
    ({ skuId, stockLevel, warehouseId }) => ({
      units: Math.max(
        (stockLevel ?? 0) -
          grid.getSkuForecastsBetweenDates(skuId, [warehouseId ?? ""], null, ctx.thresholdDay),
        0,
      ),
      costPrice: row.costPrice ?? 0,
    }),
  )
}

/** Units a variant holds beyond forecast demand to the overstock threshold. */
export function overstockQuantity(
  row: IVariantSummary,
  ctx: StockColumnCtx,
  grid: StockGridContext,
): number {
  return sum(overstockUnitsBySku(row, ctx, grid).map(({ units }) => units))
}

/** Capital tied up in overstock: overstock units valued at cost price. */
export function overstockValue(
  row: IVariantSummary,
  ctx: StockColumnCtx,
  grid: StockGridContext,
): number {
  return sum(overstockUnitsBySku(row, ctx, grid).map(({ units, costPrice }) => units * costPrice))
}

/** Earliest projected stock-out. Phased-out variants get no projection. */
export function outOfStockDate(row: IVariantSummary): Date | undefined | null {
  if (isLifecyclePhasedOut([row])) return null
  return min(row.outOfStockInformation?.flatMap((o) => o?.stockoutDates))
}

export const STOCK_LEVEL_COLUMN: StockSpec = {
  colId: ProductTableColumn.StockLevel,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.StockLevel,
      field: "stockLevel",
    }),
}

export const RESERVED_STOCK_LEVEL_COLUMN: StockSpec = {
  colId: ProductTableColumn.ReservedStockLevel,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.ReservedStockLevel,
      field: "reservedStockLevel",
    }),
}

export const AVAILABLE_TO_SELL_COLUMN: StockSpec = {
  colId: ProductTableColumn.AvailableToSell,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.AvailableToSell,
      field: "availableToSell",
    }),
}

export const LOCATION_MINIMUM_STOCK_LEVEL_COLUMN: StockSpec = {
  colId: ProductTableColumn.LocationMinimumStockLevel,
  calculate: (row) => sum(row.storageLocationData.map((location) => location.minimumStockLevel)),
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.LocationMinimumStockLevel,
      tooltipComponentParams: { tooltipType: "StorageLocation" },
      tooltip: storageLocationTooltip,
    }),
}

export const INVENTORY_VALUE_COLUMN: StockSpec = {
  colId: ProductTableColumn.InventoryValue,
  calculate: (row) => sumBy(row.stockLevelDetails ?? [], "inventoryValue"),
  colDef: (ctx) =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.InventoryValue,
      suffix: ctx.currency ?? "DKK",
      useFormatWithSuffix: true,
      decimals: 1,
      tooltip: leafVariantIds,
      tooltipComponentParams: { tooltipType: "InventoryValueGraph" },
    }),
}

export const LOST_SALES_COLUMN: StockSpec = {
  colId: ProductTableColumn.LostSales,
  calculate: (row) => row.lostSalesWithinLeadTime ?? 0,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.LostSales,
      initialWidth: 130,
    }),
}

export const OVERSTOCK_QUANTITY_COLUMN: StockSpec = {
  colId: ProductTableColumn.OverstockQuantity,
  calculate: overstockQuantity,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.OverstockQuantity,
      useFormatWithSuffix: true,
    }),
}

export const OVERSTOCK_VALUE_COLUMN: StockSpec = {
  colId: ProductTableColumn.OverstockValue,
  calculate: overstockValue,
  colDef: (ctx) =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.OverstockValue,
      decimals: 1,
      useFormatWithSuffix: true,
      suffix: ctx.currency ?? "DKK",
    }),
}

export const OUT_OF_STOCK_DATE_COLUMN: StockSpec = {
  colId: ProductTableColumn.OutOfStockDate,
  calculate: outOfStockDate,
  colDef: () =>
    createDateColumn<IVariantSummary>({
      colId: ProductTableColumn.OutOfStockDate,
      cellClass: mutedWhenPhasedOut,
      tooltip: phasedOutTooltip("Phased out. No stock-out projection"),
    }),
}

export const INCOMING_STOCK_COLUMN: StockSpec = {
  colId: ProductTableColumn.IncomingStock,
  render: (params) =>
    createElement(IncomingOrderCellRenderer, {
      ...params,
      warehouseId: params.context.warehouseId,
      enableSkuScoping: true,
    }),
  colDef: (ctx) => {
    // The order filter's scope is the same for every row; resolve it once per definition.
    const collectionIds =
      ctx.collectionIds && ctx.collectionIds.length > 0 ? ctx.collectionIds : undefined
    const externalCollectionIds = ctx.hasExternalPurchaseOrderCollectionId
      ? ctx.collections
          ?.filter((x) => ctx.collectionIds?.includes(x.id))
          .map((c) => c.externalId)
          .filter((id): id is string => id !== null && id !== undefined)
      : undefined
    const collectionPeriod =
      !ctx.hasExternalPurchaseOrderCollectionId && ctx.collectionPeriod
        ? ctx.collectionPeriod
        : undefined

    return createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.IncomingStock,
      headerSubtitle:
        collectionIds && ctx.hasExternalPurchaseOrderCollectionId ? "For collection(s)" : undefined,
      minWidth: 150,

      valueGetter: (p) => {
        const orders = filterIncomingOrders(p.data?.incomingOrders || [], {
          warehouseId: (p.context as StockGridContext).warehouseId,
          externalCollectionIds,
          collectionIds,
          collectionPeriod,
        })

        return {
          value: sumBy(orders, "quantity"),
          incomingOrders: orders,
        }
      },
      cellStyle: {
        padding: "0px 0px 0px 8px",
        margin: "0px",
      },
      aggFunc: (params) => {
        return {
          value: sumBy(params.values, "value"),
          incomingOrders: flatten(params.values.map((value) => value.incomingOrders)),
        }
      },
      comparator: (valueA, valueB) => valueA?.value - valueB?.value,
    })
  },
}

export const AVAILABILITY_WITHIN_LEAD_TIME_COLUMN: StockSpec = {
  colId: ProductTableColumn.AvailabilityWithinLeadTime,
  aggregate: (entries) => {
    const availability = availabilityOf(entries.map((e) => e.availabilityWithinLeadTime))
    return availability.kind === "ratio" ? availability.value : null
  },
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.AvailabilityWithinLeadTime,
      initialWidth: 130,
      valueGetter: (p) => {
        const pair = p.data?.availabilityWithinLeadTime
        const availability = availabilityOf(pair ? [pair] : [])
        return availability.kind === "ratio" ? availability.value : null
      },
      percentage: true,
    }),
}

let today: { day: number; weekStart: Date; byLeadTime: Map<number, Date> } | undefined
function currentDay() {
  const day = Math.floor(Date.now() / 86_400_000)
  if (today?.day !== day) {
    today = { day, weekStart: getStartOfCurrentWeekUTC(), byLeadTime: new Map() }
  }
  return today
}

function windowEnd(leadTimeDays: number): Date {
  const { weekStart, byLeadTime } = currentDay()
  let end = byLeadTime.get(leadTimeDays)
  if (!end) {
    end = addDays(weekStart, leadTimeDays)
    byLeadTime.set(leadTimeDays, end)
  }
  return end
}

export class AvailabilityTodayResult implements IAggFuncResult<number | null> {
  readonly value: number | null

  constructor(
    readonly forecast: number,
    readonly lostForecast: number,
  ) {
    this.value = availabilityTodayFromTotals([this])
  }

  toNumber() {
    return this.value
  }

  toString() {
    return this.value == null ? "" : String(this.value)
  }
}

export const AVAILABILITY_TODAY_COLUMN: StockSpec = {
  colId: ProductTableColumn.AvailabilityToday,
  calculate: (row, _ctx, { warehouseId, getSkuForecastsBetweenDates }) => {
    const warehouseIds = warehouseId ? [warehouseId] : undefined
    const { forecast, lostForecast } = availabilityTodayTotals(row, (skuId, leadTimeDays) =>
      getSkuForecastsBetweenDates(skuId, warehouseIds, null, windowEnd(leadTimeDays)),
    )
    return new AvailabilityTodayResult(forecast, lostForecast)
  },
  combine: (children) => {
    let forecast = 0
    let lostForecast = 0
    for (const child of children as (AvailabilityTodayResult | null | undefined)[]) {
      forecast += child?.forecast ?? 0
      lostForecast += child?.lostForecast ?? 0
    }
    return new AvailabilityTodayResult(forecast, lostForecast)
  },
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.AvailabilityToday,
      suffix: "%",
    }),
}

export const LOCATION_AVAILABILITY_COLUMN: StockSpec = {
  colId: ProductTableColumn.LocationAvailability,
  aggregate: (entries) => {
    const items = filterDefined(entries.flatMap((se) => se.storageLocationData))
    // Nothing forecast with nothing in stock is nothing to report; with stock on hand it reads 0.
    return locationAvailability(items) ?? (sumBy(items, "stockLevel") === 0 ? null : 0)
  },
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.LocationAvailability,
      valueGetter: (params) =>
        locationAvailability(filterDefined(params.data?.storageLocationData)) ?? 0,
      percentage: true,
      tooltipComponentParams: { tooltipType: "StorageLocation" },
      tooltip: storageLocationTooltip,
    }),
}

export const WEEKS_ON_HAND_COLUMN: StockSpec = {
  colId: ProductTableColumn.WeeksOnHand,
  calculate: (row) => calculateWeeksOnHand(row.variantForecast, row.stockLevel, 1),
  // A group's value is its leaves' distribution; it sorts by the lowest known weeks on hand.
  compare: (a, b, _ctx, isDescending) =>
    compareWithNullsLast(finiteOrNull(a), finiteOrNull(b), isDescending, numericAsc),
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.WeeksOnHand,
      cellDataType: false,
      valueFormatter: (p) => {
        if (p.node?.group) {
          // On a group row's first render AG Grid passes valueFormatter the valueGetter result,
          // not the aggFunc one, so an unexpected shape re-computes from the leaf children.
          const values: number[] = Array.isArray(p.value)
            ? p.value.filter((v: number) => v != null && isFinite(v))
            : (p.node.allLeafChildren ?? [])
                .map((leaf) => {
                  if (!leaf.data) return undefined
                  return calculateWeeksOnHand(leaf.data.variantForecast, leaf.data.stockLevel, 1)
                })
                .filter((v): v is number => v != null && isFinite(v))
          if (values.length === 0) return "N/A"
          const minVal = clampWeeksOnHand(min(values) ?? 0)
          const maxVal = clampWeeksOnHand(max(values) ?? 0)
          if (minVal === maxVal) return formatWeeksOnHand(minVal)
          return `${formatWeeksOnHand(minVal)} - ${formatWeeksOnHand(maxVal)}`
        }
        if (p.value == null || !isFinite(p.value)) return "N/A"
        return formatWeeksOnHand(Math.max(0, p.value))
      },
      cellClass: (params) => {
        if (params.node?.group) {
          const hasValues = Array.isArray(params.value)
            ? params.value.some((v: number) => v != null && isFinite(v))
            : (params.node.allLeafChildren ?? []).some(
                (leaf) =>
                  leaf.data &&
                  isFinite(
                    calculateWeeksOnHand(leaf.data.variantForecast, leaf.data.stockLevel, 1),
                  ),
              )
          if (!hasValues) return UNAVAILABLE_CELL_CLASS
          return undefined
        }
        if (params.value == null || !isFinite(params.value)) return UNAVAILABLE_CELL_CLASS
      },
      aggFunc: (p) => {
        const values: number[] = p.values
          .flatMap((v: number | number[] | null) => (Array.isArray(v) ? v : [v]))
          .filter((v): v is number => v != null)
        return values.length > 0 ? values : undefined
      },
      tooltipComponent: CustomAgGridTooltip,
      tooltipComponentParams: { tooltipType: "WeeksOnHand" },
      tooltip: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)
        // isFinite(null) is true, so an absent value has to become NaN to read as unknown.
        const values: number[] = params.node?.group
          ? Array.isArray(params.value)
            ? params.value
            : entries.map((e) => calculateWeeksOnHand(e.variantForecast, e.stockLevel, 1))
          : [params.value ?? NaN]
        const explanation = getWeeksOnHandExplanation(entries, values)

        // WeeksOnHandTooltip pairs the badge with the distribution for a group.
        if (params.node?.group) return { explanation, values }
        return explanation
          ? { unavailableMessage: explanation.message, unavailableBadge: explanation.badge }
          : undefined
      },
      decimals: 1,
    }),
}

export const STOCK_COLUMN_SPECS: readonly StockSpec[] = [
  STOCK_LEVEL_COLUMN,
  RESERVED_STOCK_LEVEL_COLUMN,
  AVAILABLE_TO_SELL_COLUMN,
  INCOMING_STOCK_COLUMN,
  LOCATION_MINIMUM_STOCK_LEVEL_COLUMN,
  INVENTORY_VALUE_COLUMN,
  LOST_SALES_COLUMN,
  AVAILABILITY_WITHIN_LEAD_TIME_COLUMN,
  AVAILABILITY_TODAY_COLUMN,
  LOCATION_AVAILABILITY_COLUMN,
  OVERSTOCK_QUANTITY_COLUMN,
  OVERSTOCK_VALUE_COLUMN,
  WEEKS_ON_HAND_COLUMN,
  OUT_OF_STOCK_DATE_COLUMN,
]
